import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import axios from "axios";
import config from "../../config";

import "../../styles/global.css";
import "../../styles/header.css";
import "../../styles/tables.css";

import MoneyCard from "../cards/moneyCard";
import Loading from "../general/loading";

import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

const PortfolioTable = () => {
    const [selectedProperty, setSelectedProperty] = useState(null);
    const navigate = useNavigate();

    const { data: properties, isLoading: isPropertiesLoading } = useQuery({
        queryKey: ["v2-properties"],
        queryFn: fetchProperties,
    });

    const { data: invoices = [], isLoading: isInvoicesLoading } = useQuery({
        queryKey: ["v2-invoices"],
        queryFn: fetchInvoices,
    });

    const { data: roles = [], isLoading: isRolesLoading } = useQuery({
        queryKey: ["v2-roles"],
        queryFn: fetchRoles,
    });

    const { data: creditBalances = [], isLoading: isBalancesLoading } = useQuery({
        queryKey: ["v2-creditBalances"],
        queryFn: fetchBalances,
    });

    const { data: propertyBalance, isLoading: isPropertyBalanceLoading } = useQuery({
        queryKey: ["v2-propertyBalance", selectedProperty?.value],
        queryFn: () => fetchPropertyBalance(selectedProperty?.value),
        enabled: !!selectedProperty?.value,
    })

    // Auto-select first property
    useEffect(() => {
        if (properties?.length > 0 && !selectedProperty) {
            setSelectedProperty(properties[0]);
        }
    }, [properties, selectedProperty]);

    const now = useMemo(() => new Date(), []);

    const accountDataMap = useMemo(() => {
        if (!selectedProperty || !creditBalances || !roles) return {};

        const map = {};

        // Loop through all roles
        roles.forEach(roleItem => {
            const { propertyRole, unit } = roleItem;
            const acc = propertyRole?.account;
            const accId = acc?._id;
            const unitPropertyId = unit?.property?._id;

            // Only include roles for the selected property
            if (!accId || !unitPropertyId || unitPropertyId !== selectedProperty.value) return;

            // Only include relevant roles
            if (!["owner", "tenant"].includes(propertyRole.role)) return;

            // Initialize map entry if missing
            if (!map[accId]) {
                map[accId] = { account: acc, units: [] };
            }

            // Add the unit if it exists and hasn't been added yet
            if (unit && !map[accId].units.find(u => u._id === unit._id)) {
                map[accId].units.push(unit);
            }
        });

        return map;
    }, [roles, creditBalances, selectedProperty]);

    const tableData = useMemo(() => Object.values(accountDataMap), [accountDataMap]);

    const accountBalances = useMemo(() => {
        const map = {};
        creditBalances?.forEach(b => {
            const accId = b.propertyRole?.account;
            if (!accId) return;
            map[accId] = b.credit || 0; // Get credit value 
        });
        return map;
    }, [creditBalances]);

    const accountInvoices = useMemo(() => {
        const map = {};
        invoices?.forEach(inv => {
            const accId = inv.chargedTo?.propertyRole?.account;
            if (!accId) return;
            if (!map[accId]) map[accId] = [];
            map[accId].push(inv); // Invoices associated with account
        });
        return map;
    }, [invoices]);

    const totalInvoiceAmount = useMemo(() => {
        return Object.keys(accountDataMap).reduce((sum, accId) => {
            const invs = (accountInvoices[accId] || []).filter(inv => inv.status !== "Paid");
            return sum + invs.reduce((s, i) => s + (i.balance || 0), 0);
        }, 0);
    }, [accountDataMap, accountInvoices]);

    const totalInvoicePastDue = useMemo(() => {
        return Object.keys(accountDataMap).reduce((sum, accId) => {
            const invs = (accountInvoices[accId] || []).filter(inv => inv.status !== "Paid");
            return sum + invs.reduce(
                (s, i) => (new Date(i.dueDate) < now ? s + (i.balance || 0) : s),
                0
            );
        }, 0);
    }, [accountDataMap, accountInvoices, now]);

    const handleRowClick = (account, units, invoiceAmount, pastDue, balance) => {
        navigate("/balance-transactions", {
            state: {
                accountName: `${account.user.givenName} ${account.user.lastName}`,
                accountId: account._id,
                property: selectedProperty?.label,
                propertyId: selectedProperty?.value,
                unit: units.map(u => u.unitNumber),
                balance,
                invoiceAmount,
                pastDue
            }
        });
    };

    const isLoading =
        isPropertiesLoading || isRolesLoading || isInvoicesLoading || isBalancesLoading || isPropertyBalanceLoading;

    return (
        <div>
            <div className="page-header">
                <Autocomplete
                    options={properties}
                    value={selectedProperty}
                    onChange={(e, newVal) => setSelectedProperty(newVal)}
                    getOptionLabel={(option) => option.label || ""}
                    renderInput={params => <TextField {...params} label="Select Property" variant="outlined" fullWidth />}
                    sx={{ width: "350px", marginBottom: "10px" }}
                />
            </div>
            {isLoading || !selectedProperty ? (
                <Loading />
            ) : (
                <>
                    <div className="card-header h-[160px] grid grid-cols-4 gap-2">
                        <MoneyCard
                            info={selectedProperty?.label}
                            color="#445485"
                        />
                        <MoneyCard
                            label="Total Property Float"
                            info={
                                <>
                                    PHP<br />
                                    {formatMoney(propertyBalance)}
                                </>
                            }
                        />
                        <MoneyCard
                            label="Total Invoice Amount"
                            info={
                                <>
                                    PHP<br />
                                    {formatMoney(totalInvoiceAmount)}
                                </>
                            }
                        />
                        <MoneyCard
                            label="Total Invoice Past Due"
                            info={
                                <>
                                    PHP<br />
                                    {formatMoney(totalInvoicePastDue)}
                                </>
                            }
                        />
                    </div>
                    <div className="page-table-container">
                        <table className="page-table">
                            <colgroup>
                                <col style={{ width: "25%" }} />
                                <col style={{ width: "15%" }} />
                                <col style={{ width: "20%" }} />
                                <col style={{ width: "20%" }} />
                                <col style={{ width: "20%" }} />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th>Account</th>
                                    <th>Unit/s</th>
                                    <th>Invoice Amount</th>
                                    <th>Invoice Past Due</th>
                                    <th>Remaining Balance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tableData?.length ? (
                                    tableData.map(row => {
                                        const { account, units } = row;
                                        const balance = accountBalances[account._id] || 0;
                                        const invs = (accountInvoices[account._id] || []).filter(i => i.status !== "Paid");
                                        const invoiceAmount = invs.reduce((sum, i) => sum + (i.totalAmount || 0), 0);
                                        const pastDue = invs.reduce(
                                            (sum, i) => (new Date(i.dueDate) < now ? sum + (i.balance || 0) : sum),
                                            0
                                        );
                                        return (
                                            <tr key={account._id} onClick={() => handleRowClick(account, units, invoiceAmount, pastDue, balance)}>
                                                <td>{account.user.givenName} {account.user.lastName}</td>
                                                <td>{units.map(u => u.unitNumber).join(", ")}</td>
                                                <td>PHP {formatMoney(invoiceAmount)}</td>
                                                <td>PHP {formatMoney(pastDue)}</td>
                                                <td>PHP {formatMoney(balance)}</td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="5" style={{ textAlign: "center" }}>No data found</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

const fetchProperties = async () => {
    try {
        const res = await axios.get(`${config.API_URL}/v2/property`, { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } });
        return res.data.map((item) => ({ value: item._id, label: item.name }));
    } catch (err) {
        console.error("Error fetching properties:", err);
        return [];
    }
};

const fetchInvoices = async () => {
    try {
        const res = await axios.get(`${config.API_URL}/v2/invoice`, { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } });
        // console.log("Invoices: ", res.data);
        return res.data;
    } catch (err) {
        console.error("Error fetching invoices:", err);
        return [];
    }
};

const fetchRoles = async () => {
    try {
        const res = await axios.get(`${config.API_URL}/v2/role/unitRole`, { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } });
        console.log("Roles: ", res.data);
        return res.data;
    } catch (err) {
        console.error("Error fetching roles:", err);
        return [];
    }
};

const fetchBalances = async () => {
    try {
        const res = await axios.get(`${config.API_URL}/v2/accountBalance/balance`, { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } });
        console.log("Balances: ", res.data);
        return res.data.balances;
    } catch (err) {
        console.error("Error fetching balances:", err);
        return [];
    }
};

const fetchPropertyBalance = async (propertyId) => {
    if (!propertyId) return 0;
    const res = await axios.get(
        `${config.API_URL}/v2/accountBalance/property/${propertyId}/total`, { headers: { Authorization: `Bearer ${localStorage.getItem('accessToken')}` } }
    );
    console.log("Total Balance: ", res.data);
    return res.data.totalBalance;
};


function formatMoney(amount) {
    const num = Number(amount) || 0;
    return num.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

export default PortfolioTable;